import i18next from 'i18next'
import { Controller } from '@hotwired/stimulus'
import { AllUnits } from '../../utils/unitUtils'
import {
  formValues,
  allowFieldEdit,
  preventFieldEdit,
  calculateWasteAllowance,
  calculateQuantityRounding,
  setFieldValidation,
} from './utils/formUtils'

// Connects to data-controller="site-task-measurements--form"
export default class extends Controller {
  static values = {
    tab: String,
    drawingUnit: String,
  }
  static targets = [
    'measurementFields',
    'quantityRounding',
    'rateBtn',
    'rateUnit',
    'submitButton',
    'totalQuantity',
    'wasteAllowance',
    'warningMessage',
  ]

  connect() {
    this.syncAllManualMeasurementsUnitsToMainUnitIfPossible()
    this.refreshUI()
    window.addEventListener('measurementTotalChange', this.measurementTotalChange.bind(this))
    document.addEventListener('turbo:frame-load', this.refreshUI.bind(this))
    this.formBlocked = false
    this.element.addEventListener('submit', this._handleFormSubmit.bind(this))
  }

  disconnect() {
    window.removeEventListener('measurementTotalChange', this.measurementTotalChange.bind(this))
    document.removeEventListener('turbo:frame-load', this.refreshUI.bind(this))
    this.element.removeEventListener('submit', this._handleFormSubmit.bind(this))
  }

  _handleFormSubmit(event) {
    if (this.formBlocked) {
      event.preventDefault()
    }
  }

  _activeMeasurementsInfos() {
    const selector = '[data-js-element="singleSiteTaskMeasurement"]:not(.d-none)'
    return Array.from(this.element.querySelectorAll(selector)).map(element => ({ ...formValues(element), element }))
  }

  refreshUI() {
    this._allowFormEdit()
    const values = this._activeMeasurementsInfos()
    values.forEach(row => {
      this._updateRowValues(row)
    })
    // console.log('values', values)

    // Keep in mind totalFromRows rounds only AFTER!
    // Consider this example: 2 rows with 500cm x 100.1cm each.
    // - It appears as two rows with total 5,01 square meters each
    // - The user might be lead to think the calculation is 10.02 m^2
    // - What we show as total: 10.01 m^2
    // WHY? Total is actually 5.005 + 5.005 = 10.010 which is then rounded to "10.01"
    const totalFromRows = values.length > 0 ? values.reduce((acc, row) => acc + row.total * row.subtract, 0) : 0
    const hasRowsWithTotals = totalFromRows !== 0
    // console.log({totalFromRows, hasRowsWithTotals})
    let startingTotal = totalFromRows

    if (!hasRowsWithTotals) {
      allowFieldEdit(this.totalQuantityTarget)
      return
    }

    if (startingTotal < 0) {
      this.totalQuantityTarget.value = startingTotal
      this._preventFormEdit(i18next.t('manual_measurements.must_be_positive', { ns: 'controllers__site_task_measurements__form_controller' }))
      return
    }

    if (AllUnits[this.rateUnitTarget.value]) {
      const selectedUnitDimensions = AllUnits[this.rateUnitTarget.value].n_dim
      const inValidFields = values.filter(row => {
        const fieldIsValid = row.dimensions.nDim === selectedUnitDimensions || row.dimensions.nDim === 0
        setFieldValidation(row.element, fieldIsValid)
        return !fieldIsValid
      })

      if (inValidFields.length > 0) {
        this._preventFormEdit(i18next.t('manual_measurements.inconsistent_units_for_dimension', { ns: 'controllers__site_task_measurements__form_controller' }))
        this.totalQuantityTarget.value = ''
        return
      }
    }

    const wasteAllowance = calculateWasteAllowance(startingTotal, this.wasteAllowanceTarget.value)
    const quantityRounding = calculateQuantityRounding(wasteAllowance, this.quantityRoundingTarget.value)
    this.totalQuantityTarget.value = quantityRounding
    preventFieldEdit(this.totalQuantityTarget, quantityRounding)
    this._allowFormEdit()
  }

  _updateRowValues(row) {
    const rowTotalEl = row.element.querySelector('[data-field="total"]')
    if (!!row.drawing_id) {
      preventFieldEdit(rowTotalEl, row.total) // Total is from drawing, prevent edit
    } else {
      row.dimensionValues = [row.length, row.width, row.height_weight].filter(v => v != 0)
      const totalFromDimensionValues = row.dimensionValues.length ? row.dimensionValues.reduce((acc, val) => acc * val, 1) : 0
      // console.log(row.dimensionValues, totalFromDimensionValues)

      if (row.dimensionValues.length === 0 || totalFromDimensionValues <= 0) {
        allowFieldEdit(rowTotalEl) // Let user edit total
      } else {
        row.total = row.same_parts * totalFromDimensionValues
        if (row.unit) {
          const unitInfo = AllUnits[row.unit]
          if (unitInfo.unit_system === 'metric') {
            row.total = row.total / unitInfo.value
          }

          preventFieldEdit(rowTotalEl, row.total) // Total is auto-calculated
        }
      }
    }
    return row
  }

  _preventFormEdit(message) {
    if (message) {
      this.warningMessageTarget.innerHTML = message
    }
    this.formBlocked = true
    this.submitButtonTarget.disabled = true
  }

  _allowFormEdit() {
    this.warningMessageTarget.innerHTML = ''
    this.formBlocked = false
    this.submitButtonTarget.disabled = false
  }

  measurementTotalChange(event) {
    const { unit, element } = event.detail

    if (this.rateUnitTarget.value != unit) {
      this.invalidateMainUnit()
    }

    const optionElement = this.rateUnitTarget.querySelector(`option[value="${unit}"]`)
    const label = optionElement ? optionElement.textContent.trim() : '?'
    element.querySelector('[data-field="measurementUnitLabel"]').innerHTML = label
    this.refreshUI()
  }

  afterAddMeasurement() {
    this.syncAllManualMeasurementsUnitsToMainUnitIfPossible()
    this.refreshUI()
  }

  handleMeasurementModeChange() { // Draw or Measurements (Calculator)
    this.refreshUI()
  }

  handleRateUnitChange() {
    this.syncAllManualMeasurementsUnitsToMainUnitIfPossible()
    this.refreshUI()
  }

  invalidateMainUnit() {
    this.rateUnitTarget.value = ''
    this._preventFormEdit()
  }

  syncAllManualMeasurementsUnitsToMainUnitIfPossible() {
    const unit = this.rateUnitTarget.value // eg. 'hour'
    const rateUnitLabel = this.rateUnitTarget.options[this.rateUnitTarget.selectedIndex].text // Eg. 'Hour'

    // When all drawing units are not the same we cannot "merge" and convert them. So we just throw an error
    const isDrawingsList = this.element.querySelectorAll('[data-field="isDrawing"]:not(.d-none *)')
    const rowUnitsEls = [...this.element.querySelectorAll('[data-field="measurementUnitValue"]:not(.d-none *)')]
    const rowTotals = [...this.element.querySelectorAll('[data-field="total"]:not(.d-none *)')]

    const inconsistententElIndexForUnits = rowUnitsEls.find((rowUnitsEl, i) => {
      return isDrawingsList[i].value === 'true' && rowUnitsEl.value != unit && rowTotals[i].value > 0 ? i : undefined
    })
    if (inconsistententElIndexForUnits) {
      this.invalidateMainUnit()
      alert(i18next.t('manual_measurements.inconsistent_units_for_drawing', { ns: 'controllers__site_task_measurements__form_controller' }))
      return
    }

    rowUnitsEls.forEach(el => el.value = unit)
    this.element.querySelectorAll('[data-field="measurementUnitLabel"]:not(.d-none *)')
      .forEach(el => el.innerHTML = rateUnitLabel)
  }
}
